<template>
  <ErrorMessage v-if="questions.length === 0">
    <p>{{ $t('question-list-manager.missing-questions') }}</p>
  </ErrorMessage>
  <article v-else class="question-list-manager" :class="theme">
    <CardHandler
      ref="cardHandler"
      :items="questions"
      :initial-index="initialQuestionIndex"
      @indexWillChange="onQuestionIndexWillChange"
      @indexChanged="onQuestionIndexChange"
    >
      <template #default="question">
        <Question
          ref="question"
          :key="question.data.Question.id"
          :data="question.data"
          :saving-answer="isQuestionSaving(question.data.Question.id)"
          :display-save-time="displaySaveTime"
          :enable-answer-lock="instantFeedback"
          :error-message="getQuestionErrorMessage(question.data.Question.id)"
          @answer="onAnswer(question.data.Question.id, ...arguments)"
        >
          <template #default>
            <p>
              <span
                class="question-index-indicator"
                :class="{
                  'display-questions-overview': displayQuestionsOverview,
                }"
                role="button"
                @click="toggleQuestionsOverview"
              >
                {{
                  $t('question-list-manager.question-index', {
                    index: question.index + 1,
                    total: questionsCount,
                  })
                }}
              </span>
            </p>
            <transition name="questions-overview-transition">
              <QuestionsOverview
                v-if="displayQuestionsOverview"
                :current-index="currentQuestionIndex"
                :lock-navigation="lockNavigation"
                :questions="questions"
                @navigate-to-index="displayQuestionByIndex"
              />
            </transition>
          </template>

          <template #below>
            <div class="next-question-button-container">
              <ThreeDButton
                v-if="allowNextButton && !isLastQuestion(question.index)"
                icon="chevron-right"
                icon-position="right"
                class="next-question-button"
                @click="displayNextQuestion"
              >
                {{ $t('question-list-manager.next-button') }}
              </ThreeDButton>
            </div>
          </template>
        </Question>

        <ThreeDButton
          v-if="!isQuestionSaved(currentQuestionId)"
          class="answer-button"
          icon="paper-plane"
          :tabindex="isQuestionChanged(currentQuestionId) ? 0 : -1"
          :disabled="!isQuestionChanged(currentQuestionId)"
          @click="saveAnswer"
        >
          {{ $t('question-list-manager.answer-button') }}
        </ThreeDButton>
      </template>
    </CardHandler>

    <menu class="question-list-manager-menu">
      <span class="question-arrow-button question-arrow-button-left">
        <ArrowButton
          direction="left"
          @click="displayPreviousQuestion"
          :disabled="lockNavigation"
          :title="$t('question-list-manager.previous-button')"
        />
      </span>
      <span class="question-arrow-button question-arrow-button-right">
        <ArrowButton
          direction="right"
          @click="displayNextQuestion"
          :disabled="lockNavigation"
          :title="$t('question-list-manager.next-button')"
        />
      </span>
    </menu>

    <ProgressBarWithButton
      :value="savedAnsweredQuestionsCount"
      :max="questionsCount"
      :tabindex="displayHandInButtonOnAllQuestions ? 0 : -1"
      :disabled="isQuestionChanged(currentQuestionId)"
      icon="inbox-in"
      @click.stop="displayConfirmHandInDialog"
    >
      {{
        allQuestionsAnswered
          ? $t('question-list-manager.hand-in-button')
          : $t('question-list-manager.finish-button')
      }}
    </ProgressBarWithButton>

    <TouchDragNavigation
      :lock-navigation="lockNavigation"
      @drag-left="displayNextQuestion"
      @drag-right="displayPreviousQuestion"
    />

    <Popup ref="popup">
      <div class="dialog-content-wrapper">
        <p class="defaultDialogContent">
          {{
            allQuestionsAnswered
              ? $t(
                  'question-list-manager.all-questions-answered-status-message'
                )
              : $t('question-list-manager.answered-questions-status-message', {
                  answeredQuestionsCount: savedAnsweredQuestionsCount,
                  totalQuestionsCount: $t('question.question-count', {
                    count: questionsCount,
                  }),
                })
          }}
        </p>

        <p class="defaultDialogContent">
          {{
            isHomework
              ? $t('question-list-manager.hand-in-homework-confirm-question')
              : $t('question-list-manager.hand-in-confirm-question')
          }}
        </p>
      </div>

      <DialogButtons :buttons="dialogButtons" />
    </Popup>
  </article>
</template>

<script>
import ArrowButton from './ArrowButton'
import CardHandler from './ui/CardHandler'
import DialogButtons from './DialogButtons'
import ErrorMessage from './ui/ErrorMessage'
import Popup from './ui/Popup'
import ProgressBarWithButton from './ProgressBarWithButton'
import Question from './Question'
import QuestionsOverview from './QuestionsOverview'
import ThreeDButton from './ThreeDButton'
import TouchDragNavigation from './ui/TouchDragNavigation'

import { QuestionState, QuestionStoreNamespacedTypes } from '@/store/questions'

import { SECOND } from '@/utils/date'
import { KeyCodes } from '~/utils/keycodes'
import { getProperty } from '@/utils/object'

import { UserAnswerModel } from '@/enums'

const relevantKeys = [
  KeyCodes.ARROW_LEFT,
  KeyCodes.ARROW_RIGHT,
  KeyCodes.END,
  KeyCodes.HOME,
  KeyCodes.ENTER,
  KeyCodes.SPACE,
]

export default {
  name: 'QuestionListManager',
  components: {
    ArrowButton,
    CardHandler,
    DialogButtons,
    ErrorMessage,
    Popup,
    ProgressBarWithButton,
    Question,
    QuestionsOverview,
    ThreeDButton,
    TouchDragNavigation,
  },
  props: {
    userAnswerModel: {
      type: Number,
      required: true,
      validator: userAnswerModel =>
        Object.values(UserAnswerModel).includes(userAnswerModel),
    },
    sessionId: {
      type: Number,
      required: true,
    },
    instantFeedback: {
      type: Boolean,
      required: true,
    },
    /**
     * A function to be called once the user pressed the "Hand in" button.
     * The function should transfer the user to some sort of evaluation view.
     */
    finishedCallback: {
      type: Function,
      required: true,
    },
    theme: {
      type: String,
    },
  },
  data() {
    return {
      allowNextButton: false,
      answers: {},
      displayQuestionsOverview: false,
      initialQuestionIndex: 0,
      isSavingForLater: false,
      currentQuestionIndex: 0,
      displayNextQuestionTimer: null,
    }
  },
  created() {
    // Set the initial question index to the first unanswered question
    // ...or the first question if all questions are already answered
    this.initialQuestionIndex = this.currentQuestionIndex = Math.max(
      this.questions.findIndex(
        question => !question.UserAnswer || !question.UserAnswer.id
      ),
      0
    )
  },
  mounted() {
    window.addEventListener('keydown', this.onKeyDown, {
      capture: true,
    })
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeyDown)
  },
  computed: {
    isHomework() {
      switch (this.userAnswerModel) {
        case UserAnswerModel.DIFFERENTIATED:
        case UserAnswerModel.STANDARD:
          return true
      }

      return false
    },
    displaySaveTime() {
      return this.isHomework && !this.instantFeedback
    },
    displayHandInButtonOnAllQuestions() {
      return this.allQuestionsAnswered
    },
    questions() {
      return this.$store.getters[
        QuestionStoreNamespacedTypes.getters.GET_QUESTIONS
      ](this.userAnswerModel, this.sessionId)
    },
    questionsCount() {
      return this.questions.length
    },
    savedAnsweredQuestionsCount() {
      return this.$store.getters[
        QuestionStoreNamespacedTypes.getters.GET_QUESTION_STATE_COUNT
      ](this.userAnswerModel, this.sessionId, QuestionState.SAVED)
    },
    allQuestionsAnswered() {
      return this.savedAnsweredQuestionsCount === this.questionsCount
    },
    currentQuestion() {
      return this.questions[this.currentQuestionIndex] || null
    },
    currentQuestionId() {
      return getProperty(this.currentQuestion, 'Question.id')
    },
    lockNavigation() {
      return (
        this.$store.getters[
          QuestionStoreNamespacedTypes.getters.GET_QUESTION_STATE_COUNT
        ](this.userAnswerModel, this.sessionId, QuestionState.CHANGED) > 0
      )
    },
    dialogButtons() {
      if (this.isHomework) {
        return [
          {
            icon: 'inbox-in',
            text: this.$t('question-list-manager.confirm-dialog-save-button'),
            callback: this.saveForLater,
            loading: this.isSavingForLater,
          },
          {
            icon: 'arrow-right',
            iconClass: 'confirm-yes-icon',
            text: this.$t(
              'question-list-manager.confirm-dialog-hand-in-button'
            ),
            callback: this.finishedCallback,
          },
        ]
      } else {
        return [
          {
            icon: 'times',
            iconClass: 'confirm-no-icon',
            text: this.$t('question-list-manager.confirm-dialog-cancel-button'),
            callback: this.closePopup,
          },
          {
            icon: 'check',
            iconClass: 'confirm-yes-icon',
            text: this.$t(
              'question-list-manager.confirm-dialog-hand-in-button'
            ),
            callback: this.finishedCallback,
          },
        ]
      }
    },
  },
  watch: {
    /**
     * Clears the timer that displays next button automatically,
     * since if the question index changes somehow before the timer finishes,
     * it's because the user has manually changed the question index somehow.
     *
     * Also disallow the next button from showing when navigating to a new question
     */
    currentQuestionIndex() {
      clearTimeout(this.displayNextQuestionTimer)
      this.allowNextButton = false
    },
  },
  methods: {
    /**
     * Everything is already saved when this function is called, so we'll just navigate the user to the home page.
     */
    saveForLater() {
      this.isSavingForLater = true
      location.href = '/'
    },

    closePopup() {
      this.$refs.popup.togglePopup(false)
    },

    /**
     * This function is needed so that Vue will detect changes in array/objects
     * and make the properties reactive all the way through.
     * @param {number} index Index of the question in the array
     * @param {Object} data the data we'd like to extend question with.
     */
    extendQuestionData(index, data) {
      this.$set(
        this.questions,
        index,
        Object.assign({}, this.questions[index], data)
      )
    },
    getQuestionState(questionId) {
      return this.$store.getters[
        QuestionStoreNamespacedTypes.getters.GET_QUESTION_STATE
      ](this.userAnswerModel, this.sessionId, questionId)
    },
    isQuestionChanged(questionId) {
      return this.getQuestionState(questionId) === QuestionState.CHANGED
    },
    isQuestionSaving(questionId) {
      return this.getQuestionState(questionId) === QuestionState.SAVING
    },
    isQuestionSaved(questionId) {
      return this.getQuestionState(questionId) === QuestionState.SAVED
    },
    isLastQuestion(questionIndex) {
      return questionIndex === this.questions.length - 1
    },
    getQuestionErrorMessage(questionId) {
      return this.$store.getters[
        QuestionStoreNamespacedTypes.getters.GET_QUESTION_ERROR_MESSAGE
      ](this.userAnswerModel, this.sessionId, questionId)
    },
    onQuestionIndexChange(newIndex) {
      this.currentQuestionIndex = newIndex
      this.toggleQuestionsOverview(false)
      this.$refs.question.focus()
    },
    onQuestionIndexWillChange() {
      this.$refs.cardHandler.$el.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      })
      this.$refs.question.blur()
    },

    /**
     * Displays the previous question.
     * @param {Event|boolean=} soft If an event is given it is ignored. If a boolean true is given the soft flag is passed on the card handler.
     */
    displayPreviousQuestion(soft) {
      if (!this.lockNavigation) {
        this.$refs.cardHandler.displayPreviousItem(soft === true)
      }
    },

    /**
     * Displays the next question.
     * @param {Event|boolean=} soft If an event is given it is ignored. If a boolean true is given the soft flag is passed on the card handler.
     */
    displayNextQuestion(soft) {
      if (!this.lockNavigation) {
        this.$refs.cardHandler.displayNextItem(soft === true)
      }
    },

    /**
     * Navigates to the question with the given index.
     * @param {number} index
     * @param {Event|boolean=} soft If an event is given it is ignored. If a boolean true is given the soft flag is passed on the card handler.
     */
    displayQuestionByIndex(index, soft) {
      if (!this.lockNavigation) {
        this.$refs.cardHandler.displayItemByIndex(index, soft === true)
      }
    },

    /**
     * Toggles the display state for the questions overview.
     * @param {boolean=} newState A new optional state. If no state is given, the new state will be the opposite of the current state.
     */
    toggleQuestionsOverview(newState) {
      this.displayQuestionsOverview =
        typeof newState === 'boolean'
          ? newState
          : !this.displayQuestionsOverview
    },

    onKeyDown(event) {
      let eventKey = event.key
      let preventDefault = false

      if (
        [KeyCodes.ARROW_LEFT, KeyCodes.ARROW_RIGHT].includes(eventKey) &&
        this.lockNavigation
      ) {
        return
      }

      if (relevantKeys.includes(eventKey)) {
        preventDefault = true

        switch (eventKey) {
          case KeyCodes.ARROW_RIGHT:
            this.displayNextQuestion()
            break

          case KeyCodes.ARROW_LEFT:
            this.displayPreviousQuestion()
            break

          case KeyCodes.END:
            this.displayQuestionByIndex(this.questionsCount)
            break

          case KeyCodes.HOME:
            this.displayQuestionByIndex(0)
            break

          case KeyCodes.ENTER:
          case KeyCodes.SPACE:
            if (event.target.nodeName !== 'button') {
              if (this.isQuestionChanged(this.currentQuestionId)) {
                this.saveAnswer()
              }
            } else {
              preventDefault = false
            }

            break
        }
      }

      if (preventDefault) {
        event.preventDefault()
      }
    },

    /**
     * Handler for when user has picked or changed an answer
     * @param {number} questionId
     * @param {number|string} userAnswer
     * @param {number} lapsedTime
     */
    onAnswer(questionId, userAnswer, lapsedTime) {
      this.$store.dispatch(
        QuestionStoreNamespacedTypes.actions.SELECT_USER_ANSWER,
        {
          userAnswerModel: this.userAnswerModel,
          sessionId: this.sessionId,
          questionId,
          userAnswer,
          lapsedTime,
        }
      )
    },

    /**
     * Handler for when user has caused the answer to be saved to the server
     * @returns {Promise<T>}
     */
    saveAnswer() {
      const questionIndex = this.currentQuestionIndex

      // Allow the next button to be shown, now that an answer has been given and we might display the answer evaluation
      this.allowNextButton = true

      this.$store
        .dispatch(QuestionStoreNamespacedTypes.actions.SUBMIT_USER_ANSWER, {
          userAnswerModel: this.userAnswerModel,
          sessionId: this.sessionId,
          questionId: this.currentQuestionId,
        })
        .then(data => {
          if (this.instantFeedback && !data.Question) {
            throw this.$t(
              'question-list-manager.no-question-data-in-save-response'
            )
          }

          if (
            !this.isLastQuestion(questionIndex) &&
            ((data['UserAnswer'] && data['UserAnswer']['correct']) ||
              !this.instantFeedback)
          ) {
            this.displayNextQuestionTimer = setTimeout(
              function() {
                this.displayNextQuestion(true)
              }.bind(this),
              SECOND
            )
          }
        })
    },

    /**
     * Displays a confirmation popup for handing in the assignment.
     */
    displayConfirmHandInDialog() {
      this.$refs.popup.togglePopup(true)
    },
  },
}
</script>

<style lang="scss" scoped>
// Magic number follows the question max-width
$maxWidthMagicNumber: 40rem;

// 6 is 8*12/16, sine arrow buttons are 4em each, and we want to add the EM but as REM because the $maxWidthMagicNumber is in REM
$arrowButtonsTogglePoint: $maxWidthMagicNumber + 6;

// Theme variables
// Indskoling
$theme1MainColor: #ff9800;
$theme1SecondaryColor: #008ecf;
$theme1ShadowColor: #ef7c04;
$theme1BorderRadius: 1em;

// Mellemtrin
$theme2MainColor: #103b7a;
$theme2SecondaryColor: #e85b5b;
$theme2ShadowColor: #4c709b;
$theme2BorderRadius: 0;

// Udskoling
$theme3MainColor: #00a099;
$theme3SecondaryColor: #103b7a;
$theme3ShadowColor: #05756d;
$theme3BorderRadius: 0;

::v-deep * {
  -webkit-tap-highlight-color: transparent;
}

// Override Fessor Style
h1,
h2,
h3,
h4 {
  font-family: $headFont;
  font-weight: bold;
}

.question-list-manager {
  --theme-main-color: #{$theme3MainColor};
  --theme-secondary-color: #{$theme3SecondaryColor};
  --theme-shadow-color: #{$theme3ShadowColor};
  --theme-border-radius: #{$theme3BorderRadius};

  position: relative;
  padding: 0.5em 0;
  font-family: $breadTextFont;
  font-weight: $fontWeightSemiBold;
  font-size: 1em;
  // This prevents the page from scrolling horizontally when swiping between questions
  touch-action: pan-y pinch-zoom;

  .question {
    margin: auto;
  }

  &.theme1 {
    --theme-main-color: #{$theme1MainColor};
    --theme-secondary-color: #{$theme1SecondaryColor};
    --theme-shadow-color: #{$theme1ShadowColor};
    --theme-border-radius: #{$theme1BorderRadius};
  }

  &.theme2 {
    --theme-main-color: #{$theme2MainColor};
    --theme-secondary-color: #{$theme2SecondaryColor};
    --theme-shadow-color: #{$theme2ShadowColor};
    --theme-border-radius: #{$theme2BorderRadius};
  }
}

.question-index-indicator {
  position: relative;
  display: inline-block;
  margin-top: 1em;
  padding: 0.25em 1em;
  font-size: 0.75em;
  font-weight: $fontWeightSemiBold;
  text-transform: uppercase;
  color: white;
  background: var(--theme-secondary-color);
  border-radius: var(--theme-border-radius);
  z-index: 3; // Make sure the button is placed above the overview when animating in
  transition: border-radius 0.15s ease-out;

  &.display-questions-overview {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  &:after {
    content: '▾';
    display: inline-block;
    padding-left: 0.9em;
    margin-left: 0.5em;
    border-left: 1px solid rgba(255, 255, 255, 0.2);
  }
}

.questions-overview {
  margin-top: -0.833em;
  border: 0.2em solid var(--theme-secondary-color);
  border-radius: var(--theme-border-radius);
  border-top-left-radius: 0;
  z-index: 2; // Make sure the overview is placed above the validation icon for the question
}

.questions-overview-transition-enter-active,
.questions-overview-transition-leave-active {
  transition: all 0.15s ease-out;
}

.questions-overview-transition-enter,
.questions-overview-transition-leave-to {
  opacity: 0;
  transform: translateY(-2em);
}

.answer-button {
  --button-color: var(--theme-main-color);
  --button-shadow-color: var(--theme-shadow-color);

  display: block;
  margin: -2.4em auto 0;
  font-size: 1.5em;
}

::v-deep .card-handler .card-handler-child {
  padding: 0 2em;
}

.next-question-button-container {
  position: absolute;
  display: block;
  bottom: 0;
  left: 0;
  right: 0;
  transform: translateY(50%);
  text-align: center;
}

.question-list-manager-menu {
  > .question-arrow-button {
    --arrow-button-size: 3em;
    $centerFragment: calc((50% - (#{$maxWidthMagicNumber} / 2)) / 2);

    position: absolute;
    display: block;
    top: 15rem;
    z-index: 1;
    border-radius: 100%;

    > .arrow-button {
      // !important is to override style bleeding in from the global Fessor style
      color: var(--theme-secondary-color) !important;
    }

    &.question-arrow-button-right {
      right: $centerFragment;
      transform: translateX(50%);
    }

    &.question-arrow-button-left {
      left: $centerFragment;
      transform: translateX(-50%);
    }

    @media (max-width: $arrowButtonsTogglePoint) {
      display: none;
    }
  }
}

.progress-bar-with-button {
  width: calc(100% - 4em);
  max-width: $maxWidthMagicNumber;
  margin: 2em auto 1.5em !important;
}

::v-deep .popup {
  .confirm-yes-icon {
    color: $successText;
  }

  .confirm-no-icon {
    color: $errorText;
  }
}

.touch-drag-navigation {
  color: var(--theme-secondary-color);
}
</style>
